import { useLayoutEffect } from 'react';
import { create } from 'zustand';
import { useSidebar } from '../../stores/uiStore';

/**
 * Which 1:1 design board the app shell is currently showing (web mode only), and the shell
 * choices that follow from it.
 *
 * The canvas shows each board with only its own left navigation plus the shared top bar (TopNav),
 * and a 1:1 board renders both itself, so while a design board is mounted the app shell draws no
 * sidebar or header of its own (DashboardLayout). The sidebar state below still applies to the
 * working pages; on design boards it is never shown.
 */
interface BoardChromeState {
  board: string | null;
  /** Mount count so overlapping mounts (route transitions, StrictMode) do not clear each other. */
  mounts: number;
  /** User expanded the sidebar while on a design board (session only). */
  designSidebarExpanded: boolean;
  enter: (board: string) => void;
  leave: () => void;
  toggleDesignSidebar: () => void;
}

export const useBoardChrome = create<BoardChromeState>((set) => ({
  board: null,
  mounts: 0,
  designSidebarExpanded: false,
  enter: (board) => set((s) => ({ board, mounts: s.mounts + 1 })),
  leave: () =>
    set((s) => {
      const mounts = Math.max(0, s.mounts - 1);
      // the latest enter() wins; the shell leaves design mode only when no board is mounted
      return { mounts, board: mounts === 0 ? null : s.board };
    }),
  toggleDesignSidebar: () => set((s) => ({ designSidebarExpanded: !s.designSidebarExpanded })),
}));

/** Register a mounted design board with the shell (before paint, so the rail applies on first frame). */
export function useRegisterDesignBoard(board: string, enabled: boolean): void {
  useLayoutEffect(() => {
    if (!enabled) return undefined;
    const { enter, leave } = useBoardChrome.getState();
    enter(board);
    return () => leave();
  }, [board, enabled]);
}

/** Sidebar collapse as the shell should apply it: icon rail by default on design boards. */
export function useShellSidebar() {
  const sidebar = useSidebar();
  const onDesignBoard = useBoardChrome((s) => s.board !== null);
  const designExpanded = useBoardChrome((s) => s.designSidebarExpanded);
  const toggleDesign = useBoardChrome((s) => s.toggleDesignSidebar);
  return {
    ...sidebar,
    onDesignBoard,
    isCollapsed: onDesignBoard ? !designExpanded : sidebar.isCollapsed,
    toggleCollapse: onDesignBoard ? toggleDesign : sidebar.toggleCollapse,
  };
}
